import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api";
import { inr, inrFull } from "@/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  IndianRupee, TrendingUp, TrendingDown, Building2, Sparkles, ArrowUpRight, MapPin,
} from "lucide-react";
import { Link } from "react-router-dom";

const LEAD_COLORS = {
  new: "#71717a", contacted: "#3b82f6", site_visit: "#8b5cf6",
  quotation: "#f59e0b", negotiation: "#ec4899", booked: "#10b981", lost: "#ef4444",
};

function Kpi({ label, value, sub, trend, icon: Icon, testid }) {
  return (
    <Card data-testid={testid} className="border-zinc-200 shadow-sm hover:shadow-md transition-shadow bg-white">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-zinc-500">{label}</div>
          {Icon && <Icon size={16} className="text-emerald-700" />}
        </div>
        <div className="kpi-num mt-2 text-zinc-950">{value}</div>
        {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
        {trend != null && (
          <div className={`inline-flex items-center gap-1 mt-2 text-xs font-medium ${trend >= 0 ? "text-emerald-700" : "text-red-600"}`}>
            {trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {Math.abs(trend).toFixed(1)}% vs last month
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [projectId, setProjectId] = useState("__all__");
  const scoped = projectId && projectId !== "__all__" ? projectId : undefined;

  const kpiQ = useQuery({
    queryKey: ["dashboard-kpi", scoped || "all"],
    queryFn: async () => (await api.get("/dashboard/kpi", { params: scoped ? { project_id: scoped } : {} })).data,
  });
  const projectsQ = useQuery({
    queryKey: ["projects-summary"],
    queryFn: async () => (await api.get("/projects")).data,
  });
  const unitsQ = useQuery({
    queryKey: ["dashboard-units", scoped || "all"],
    queryFn: async () => {
      if (scoped) return (await api.get("/units", { params: { project_id: scoped } })).data;
      // combined view — pull units per project and merge
      const list = (await api.get("/projects")).data;
      const all = await Promise.all(
        list.map((p) => api.get("/units", { params: { project_id: p.id } }).then((r) => r.data.map((u) => ({ ...u, _project_name: p.name })))),
      );
      return all.flat();
    },
  });

  const data = kpiQ.data;
  const projects = useMemo(() => projectsQ.data || [], [projectsQ.data]);
  const selectedProject = useMemo(
    () => projects.find((p) => p.id === scoped),
    [projects, scoped],
  );

  const trendData = useMemo(() => {
    if (!data?.monthly_trend) return [];
    return data.monthly_trend.map((m) => ({ ...m, month: m.month.slice(5) }));
  }, [data]);

  const leadPieData = useMemo(() => {
    if (!data?.leads_by_status) return [];
    return Object.entries(data.leads_by_status).map(([k, v]) => ({ name: k, value: v }));
  }, [data]);

  const pnl = data?.pnl || {};
  const marginPct = pnl.total_revenue ? ((pnl.gross_profit / pnl.total_revenue) * 100).toFixed(1) : "0.0";

  const units = useMemo(() => unitsQ.data || [], [unitsQ.data]);
  const soldUnits = useMemo(() => {
    const SOLD = new Set(["booked", "agreement_done", "registered", "sold"]);
    return units.filter((u) => SOLD.has(u.status));
  }, [units]);
  const remainingUnits = useMemo(() => units.filter((u) => u.status === "available"), [units]);

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">
            {selectedProject ? "Project Dashboard" : "Executive Overview"}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-zinc-950 mt-1">
            {selectedProject ? selectedProject.name : "Every project, one command centre"}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {selectedProject
              ? <>Live financials, inventory and sales for <b>{selectedProject.name}</b> — {selectedProject.location}.</>
              : "Combined live financials, inventory and sales across all Param Developers projects."}
          </p>
        </div>
        <div className="flex flex-col md:flex-row items-stretch md:items-end gap-2">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-72 h-10" data-testid="dash-project-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All projects (combined)</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} · <span className="text-zinc-500">{p.code}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="w-fit border-emerald-300 bg-emerald-50 text-emerald-800 font-mono">
            <Sparkles size={12} className="mr-1" /> GPT 5.6 Terra powered
          </Badge>
        </div>
      </div>

      {selectedProject && (
        <Card className="bg-gradient-to-r from-emerald-50 to-white border-emerald-200" data-testid="dash-project-summary">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-20 h-20 rounded-md bg-zinc-100 overflow-hidden shrink-0 border border-zinc-200">
              {selectedProject.thumbnail_url && <img src={selectedProject.thumbnail_url + (selectedProject.updated_at ? `?v=${encodeURIComponent(selectedProject.updated_at)}` : "")} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-widest bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded">{selectedProject.code}</span>
                {selectedProject.ownership_type === "jda" ? (
                  <Badge className="bg-amber-500 text-white hover:bg-amber-500">JDA</Badge>
                ) : (
                  <Badge className="bg-emerald-700 hover:bg-emerald-700">Company Owned</Badge>
                )}
                <span className="text-[11px] uppercase tracking-widest text-zinc-500">{selectedProject.project_type}</span>
              </div>
              <div className="mt-1 text-sm text-zinc-600 flex items-center gap-1"><MapPin size={12} /> {selectedProject.location}</div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-2 text-[11px]">
                <div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Towers</div><div className="font-mono font-bold">{selectedProject.number_of_towers || 0}</div></div>
                <div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Wings</div><div className="font-mono font-bold">{selectedProject.number_of_wings || 0}</div></div>
                <div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Floors</div><div className="font-mono font-bold">{selectedProject.total_floors || 0}</div></div>
                <div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Flats</div><div className="font-mono font-bold">{selectedProject.number_of_flats || 0}</div></div>
                <div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Shops</div><div className="font-mono font-bold">{selectedProject.number_of_shops || 0}</div></div>
                <div><div className="text-[9px] uppercase tracking-widest text-zinc-500">RERA</div><div className="font-mono font-bold truncate">{selectedProject.rera_number || "—"}</div></div>
              </div>
            </div>
            <Link
              to={`/projects/${selectedProject.id}`}
              className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1 shrink-0"
              data-testid="dash-open-project"
            >
              Open project <ArrowUpRight size={12} />
            </Link>
          </CardContent>
        </Card>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Kpi testid="kpi-revenue" label="Total Revenue" value={inr(pnl.total_revenue)} sub={inrFull(pnl.total_revenue)} icon={IndianRupee} />
        <Kpi testid="kpi-expense" label="Total Expense" value={inr(pnl.total_expense)} sub={inrFull(pnl.total_expense)} icon={TrendingDown} />
        <Kpi testid="kpi-gross-profit" label="Gross Profit" value={inr(pnl.gross_profit)} sub={`${marginPct}% margin`} icon={TrendingUp} />
        <Kpi testid="kpi-loan-out" label="Loan Outstanding" value={inr(pnl.loan_outstanding)} sub="Bank + private" />
        <Kpi testid="kpi-sold-value" label="Booked Value" value={inr(pnl.sold_units_value)} sub={`${pnl.sold_units_count || 0} units`} />
        <Kpi testid="kpi-projects" label="Active Projects" value={data?.project_count ?? 0} sub={`${pnl.total_units || 0} units total`} icon={Building2} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-white border-zinc-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Revenue vs Expense — Last 6 Months</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => inr(v)} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }}
                  formatter={(v) => inrFull(v)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="expense" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-white border-zinc-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Leads by Stage</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            {leadPieData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={leadPieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {leadPieData.map((e, i) => (
                      <Cell key={e.name || `lead-${i}`} fill={LEAD_COLORS[e.name] || "#a1a1aa"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-zinc-500">No leads yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sold / Remaining units */}
      <UnitsSplitPanel soldUnits={soldUnits} remainingUnits={remainingUnits} scoped={!!scoped} />

      {/* Projects list — hidden when a specific project is selected */}
      {!selectedProject && (
      <Card className="bg-white border-zinc-200 shadow-sm">
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="font-heading text-base">Active Projects</CardTitle>
          <Link to="/projects" className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid="see-all-projects">
            See all <ArrowUpRight size={12} />
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 divide-x divide-y divide-zinc-200 md:divide-y-0">
            {projects.slice(0, 6).map((p) => {
              const s = p.unit_stats || {};
              const sold = (s.booked || 0) + (s.agreement_done || 0) + (s.registered || 0) + (s.sold || 0);
              const pct = s.total ? Math.round((sold / s.total) * 100) : 0;
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setProjectId(p.id)}
                  className="block p-5 data-row text-left w-full"
                  data-testid={`dash-project-${p.code}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-md bg-zinc-100 overflow-hidden shrink-0 border border-zinc-200">
                      {p.thumbnail_url && <img src={p.thumbnail_url + (p.updated_at ? `?v=${encodeURIComponent(p.updated_at)}` : "")} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-sm truncate">{p.name}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{p.location}</div>
                      <div className="mt-1 flex gap-2 items-center">
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{p.code}</span>
                        {p.ownership_type === "jda" && (
                          <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded">JDA</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-[11px] text-zinc-500">
                      <span>Inventory sold</span>
                      <span className="font-mono">{sold}/{s.total || 0} · {pct}%</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-zinc-100 rounded">
                      <div className="h-1.5 bg-emerald-600 rounded" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </button>
              );
            })}
            {!projects.length && (
              <div className="col-span-full p-10 text-center text-sm text-zinc-500">
                No projects yet — <Link to="/projects/new" className="text-emerald-700 underline">create your first project</Link>.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
}


// ============ Sold vs Remaining Units Panel ============
function UnitsSplitPanel({ soldUnits, remainingUnits, scoped }) {
  const [tab, setTab] = useState("sold");
  const rows = tab === "sold" ? soldUnits : remainingUnits;
  const total = soldUnits.length + remainingUnits.length;
  const soldValue = soldUnits.reduce((s, u) => s + (u.final_selling_price || u.price || 0), 0);
  const remainingValue = remainingUnits.reduce((s, u) => s + (u.final_selling_price || u.price || 0), 0);

  const STATUS_LABEL = {
    booked: "Booked", agreement_done: "Agreement", registered: "Registered", sold: "Sold", available: "Available",
  };

  return (
    <Card className="bg-white border-zinc-200 shadow-sm" data-testid="units-split-panel">
      <CardHeader className="pb-2 flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Building2 size={16} /> Inventory Ledger
          <span className="text-xs font-normal text-zinc-500 ml-2">({total} total)</span>
        </CardTitle>
        <div className="flex bg-zinc-100 rounded-md p-0.5">
          <button
            onClick={() => setTab("sold")}
            className={`text-xs px-3 py-1.5 rounded ${tab === "sold" ? "bg-white shadow-sm text-emerald-700 font-semibold" : "text-zinc-600"}`}
            data-testid="units-tab-sold"
          >
            Sold <span className="font-mono ml-1">{soldUnits.length}</span>
          </button>
          <button
            onClick={() => setTab("remaining")}
            className={`text-xs px-3 py-1.5 rounded ${tab === "remaining" ? "bg-white shadow-sm text-amber-700 font-semibold" : "text-zinc-600"}`}
            data-testid="units-tab-remaining"
          >
            Remaining <span className="font-mono ml-1">{remainingUnits.length}</span>
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="px-5 py-2 border-b border-zinc-200 bg-zinc-50 grid grid-cols-3 gap-3 text-xs">
          <div>
            <span className="text-zinc-500">Sold value:</span> <b className="font-mono text-emerald-700">{inr(soldValue)}</b>
          </div>
          <div>
            <span className="text-zinc-500">Remaining value:</span> <b className="font-mono text-amber-700">{inr(remainingValue)}</b>
          </div>
          <div>
            <span className="text-zinc-500">Occupancy:</span>{" "}
            <b className="font-mono">{total ? Math.round((soldUnits.length / total) * 100) : 0}%</b>
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500">
            No {tab === "sold" ? "sold" : "available"} units{scoped ? " in this project" : ""} yet.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-white border-b border-zinc-200 sticky top-0">
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-5 py-2 font-semibold">Unit</th>
                  <th className="text-left px-3 py-2 font-semibold">Config · Area</th>
                  {!scoped && <th className="text-left px-3 py-2 font-semibold">Project</th>}
                  {tab === "sold" && <th className="text-left px-3 py-2 font-semibold">Buyer</th>}
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="text-right px-5 py-2 font-semibold">Price</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50 data-row" data-testid={`unit-row-${u.unit_number}`}>
                    <td className="px-5 py-2 font-mono font-semibold">{u.unit_number}</td>
                    <td className="px-3 py-2 text-xs text-zinc-600">
                      {u.configuration || u.unit_type || "—"}
                      <span className="text-zinc-400"> · {u.super_built_up_area || u.area_sqft || 0} sqft</span>
                    </td>
                    {!scoped && <td className="px-3 py-2 text-xs text-zinc-600 truncate max-w-[180px]">{u._project_name || "—"}</td>}
                    {tab === "sold" && (
                      <td className="px-3 py-2 text-xs">
                        {u.customer_name ? (
                          <>
                            <div className="font-medium text-zinc-800">{u.customer_name}</div>
                            <div className="text-zinc-500 font-mono text-[10px]">{u.customer_phone || ""}</div>
                          </>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <span
                        className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                          u.status === "registered" || u.status === "sold"
                            ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                            : u.status === "agreement_done"
                            ? "bg-blue-100 text-blue-800 border-blue-300"
                            : u.status === "booked"
                            ? "bg-amber-100 text-amber-800 border-amber-300"
                            : "bg-zinc-100 text-zinc-700 border-zinc-300"
                        }`}
                      >
                        {STATUS_LABEL[u.status] || u.status}
                      </span>
                    </td>
                    <td className="px-5 py-2 text-right font-mono">{inr(u.final_selling_price || u.price || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
